import { evaluate, parseFormula, type TSeriesLookup } from "@libs/ledger-formula";
import { buildLedgerGraph, seriesLookupOf, solveLedger, type TLedgerGraph } from "./engine";
import type { TLedgerFigure } from "@/zapi-frontend/shared/types/modelLedger";
import type { TRqaSimTwin, TTwinBlock, TTwinVar } from "@/zapi-frontend/shared/types/rqaSim";
import { figureFormat, fillBlock, numberInCell, spellSlot } from "@libs/figure-spans";

// What the reader has chosen: which option of each dimension, which segments are in view, and
// where they have dragged each knob. Everything the page shows is a function of this and the ledger.
export type TTwinSelection = {
  options: Record<string, string>;
  inView: string[];
  knobs: Record<string, number>;
};

export const varExpr = (declared: TTwinVar | undefined): string =>
  typeof declared === "string" ? declared : declared?.expr ?? "";
export const varUnit = (declared: TTwinVar | undefined): string | null =>
  typeof declared === "string" ? null : declared?.unit ?? null;
export const varName = (declared: TTwinVar | undefined): string | null =>
  typeof declared === "string" ? null : declared?.name ?? null;
const varOver = (declared: TTwinVar | undefined): "selection" | "everything" =>
  typeof declared !== "string" && declared?.over === "everything" ? "everything" : "selection";

export type TTouched = Partial<TTwinSelection> | null;

export const pickOption = (twin: TRqaSimTwin, touched: TTouched, dimensionId: string, optionId: string): TTouched => {
  const dimension = twin.dimensions.find((candidate) => candidate.id === dimensionId);
  const knobs = { ...(touched?.knobs ?? {}) };
  for (const key of dimension ? dimensionKeys(dimension) : []) delete knobs[key];
  return { ...(touched ?? {}), knobs, options: { ...(touched?.options ?? {}), [dimensionId]: optionId } };
};

export const dragKnob = (touched: TTouched, key: string, value: number): TTouched => ({
  ...(touched ?? {}),
  knobs: { ...(touched?.knobs ?? {}), [key]: value }
});

export const setMembership = (touched: TTouched, inView: string[]): TTouched => ({
  ...(touched ?? {}),
  inView
});

export const resetTwin = (): TTouched => null;

export const selectionOf = (twin: TRqaSimTwin, touched: TTouched): TTwinSelection => {
  const opening = baseSelection(twin);
  return {
    options: { ...opening.options, ...(touched?.options ?? {}) },
    inView: touched?.inView ?? opening.inView,
    knobs: { ...(touched?.knobs ?? {}) }
  };
};

export const baseSelection = (twin: TRqaSimTwin): TTwinSelection => ({
  options: Object.fromEntries(
    twin.dimensions.map((dimension) => [dimension.id, dimension.base ?? dimension.options[0]?.id ?? ""])
  ),
  inView: twin.segments?.base ?? twin.segments?.members.map((member) => member.id) ?? [],
  knobs: {}
});

const NO_LEDGER_KEY = () => false;

const settingsOf = (
  dimension: TRqaSimTwin["dimensions"][number],
  option: TRqaSimTwin["dimensions"][number]["options"][number] | undefined,
  ledgerHolds: (key: string) => boolean
): Record<string, number> => {
  const set = option?.set ?? {};
  if (option?.id !== (dimension.base ?? dimension.options[0]?.id)) return set;
  return Object.fromEntries(Object.entries(set).filter(([key]) => !ledgerHolds(key)));
};

// The scenario the chips name, then the segments out of view, then where the reader has dragged.
const overridesFor = (
  twin: TRqaSimTwin,
  selection: TTwinSelection,
  gated: boolean,
  ledgerHolds: (key: string) => boolean
): Record<string, number> => {
  const overrides: Record<string, number> = {};
  for (const dimension of twin.dimensions) {
    const option = dimension.options.find((candidate) => candidate.id === selection.options[dimension.id]);
    Object.assign(overrides, settingsOf(dimension, option, ledgerHolds));
  }
  if (gated && twin.segments) {
    for (const member of twin.segments.members) {
      if (selection.inView.includes(member.id)) continue;
      // Out of view means the case read without this part of it, so a gate writes zero.
      for (const key of member.gates ?? [member.figure]) overrides[key] = 0;
    }
  }
  for (const [key, value] of Object.entries(selection.knobs)) {
    if (Number.isFinite(value)) overrides[key] = value;
  }
  return overrides;
};

// Vars are twin-local arithmetic over figures and other vars. A var declared `everything` reads at
// the full membership: a portfolio total computed over the selection is not a portfolio total.
const solveVars = (
  twin: TRqaSimTwin,
  selection: Map<string, number>,
  everything: Map<string, number>,
  seriesMembers: TSeriesLookup
) => {
  const values = new Map<string, number>();
  const names = Object.keys(twin.vars);
  // Up to one pass per var, so a var reading another settles whatever order they were written in.
  for (let round = 0; round < names.length + 1; round++) {
    let settled = true;
    for (const name of names) {
      if (values.has(name)) continue;
      const scope = varOver(twin.vars[name]) === "everything" ? everything : selection;
      const env: Record<string, number> = {};
      for (const [key, value] of scope) env[key] = value;
      for (const [key, value] of values) env[key] = value;
      let computed: number | null = null;
      try {
        const result = evaluate(parseFormula(varExpr(twin.vars[name])), env, seriesMembers);
        if (typeof result === "number" && Number.isFinite(result)) computed = result;
      } catch {
        computed = null;
      }
      if (computed === null) settled = false;
      else values.set(name, computed);
    }
    if (settled) break;
  }
  // A var that never settles is dropped from the map, and a box or a ref naming it then draws a
  // dash with nothing saying why. Naming them lets the page account for them as it does figures.
  const missing = names.filter((name) => !values.has(name));
  return { values, missing };
};

export type TTwinSolution = {
  values: Map<string, number>;
  baseValues: Map<string, number>;
  // The same knob positions read without the membership's gates applied: what the case is worth
  // whole. A segment chip is sized from here, so a reader sees what they have set aside rather
  // than the zero the gate wrote over it.
  wholeValues: Map<string, number>;
  unresolved: string[];
  noReturn: string[];
};

/** Every key any option of a dimension can set. A chip stands for the whole configuration its
 *  row can express, so a key one sibling option moves is part of it too. */
export const dimensionKeys = (dimension: TRqaSimTwin["dimensions"][number]): string[] => {
  const out = new Set<string>();
  for (const option of dimension.options) for (const key of Object.keys(option.set)) out.add(key);
  return [...out];
};

const holds = (
  dimension: TRqaSimTwin["dimensions"][number],
  option: TRqaSimTwin["dimensions"][number]["options"][number] | undefined,
  values: ReadonlyMap<string, number>,
  base: ReadonlyMap<string, number>,
  ledgerHolds: (key: string) => boolean
): boolean => {
  const settings = settingsOf(dimension, option, ledgerHolds);
  for (const key of dimensionKeys(dimension)) {
    const set = settings[key];
    const want = typeof set === "number" && Number.isFinite(set) ? set : base.get(key);
    if (want === undefined ? values.has(key) : values.get(key) !== want) return false;
  }
  return true;
};

/** Which option a row is showing, read off the numbers rather than off a stored choice. A stored
 *  choice stays lit after the reader drags one of the row's sliders, and the row then reads the
 *  memo's scenario, with the memo's note, over a number that is not the memo's. Derived from the
 *  values, a row whose numbers match no option lights nothing, which is the honest answer. */
export const litOption = (
  dimension: TRqaSimTwin["dimensions"][number],
  chosenId: string | undefined,
  values: ReadonlyMap<string, number>,
  base: ReadonlyMap<string, number>,
  ledgerHolds: (key: string) => boolean = NO_LEDGER_KEY
) => {
  const lit = (option: TRqaSimTwin["dimensions"][number]["options"][number] | undefined) =>
    holds(dimension, option, values, base, ledgerHolds);
  const chosen = dimension.options.find((option) => option.id === chosenId);
  if (lit(chosen)) return chosen;
  const declared = dimension.options.find((option) => option.id === (dimension.base ?? dimension.options[0]?.id));
  if (declared !== chosen && lit(declared)) return declared;
  return dimension.options.find(lit);
};

export const solveTwin = (
  twin: TRqaSimTwin,
  graph: TLedgerGraph,
  selection: TTwinSelection
): TTwinSolution => {
  const ledgerHolds = (key: string) => graph.byKey.has(key);
  const current = solveLedger(graph, overridesFor(twin, selection, true, ledgerHolds));
  const everything = solveLedger(graph, overridesFor(twin, selection, false, ledgerHolds));
  const opening = baseSelection(twin);
  const base = solveLedger(graph, overridesFor(twin, opening, true, ledgerHolds));
  // A membership whose `base` is a subset opens with some segments already out of view, so the
  // baseline for an `everything` var has to be solved ungated too, exactly as the current one is.
  const baseEverything = solveLedger(graph, overridesFor(twin, opening, false, ledgerHolds));

  const values = new Map(current.values);
  const seriesMembers = seriesLookupOf(graph);
  const solvedVars = solveVars(twin, current.values, everything.values, seriesMembers);
  for (const [name, value] of solvedVars.values) values.set(name, value);

  const baseValues = new Map(base.values);
  for (const [name, value] of solveVars(twin, base.values, baseEverything.values, seriesMembers).values) {
    baseValues.set(name, value);
  }

  return {
    values,
    baseValues,
    wholeValues: everything.values,
    unresolved: [...current.unresolved, ...solvedVars.missing],
    noReturn: current.noReturn
  };
};

export const twinGraph = (figures: TLedgerFigure[]): TLedgerGraph => buildLedgerGraph(figures);

/** What a knob may be dragged across. The ledger's own input is the source where it declares one:
 *  the figure knows what values it may take, and a twin repeating them is a second answer. */
export const knobRange = (
  knob: { key: string; min?: number; max?: number; step?: number },
  graph: TLedgerGraph
): { min: number; max: number; step: number } | null => {
  const declared = graph.byKey.get(knob.key)?.range ?? null;
  if (declared) return declared;
  if (knob.min !== undefined && knob.max !== undefined && knob.step !== undefined) {
    return { min: knob.min, max: knob.max, step: knob.step };
  }
  return null;
};

export const knobStart = (knobKey: string, base: number | undefined, graph: TLedgerGraph): number =>
  graph.byKey.get(knobKey)?.value ?? base ?? 0;

/** A knob whose every segment has left the page is drawn but not draggable — a control whose
 *  subject is gone has to say so, and hiding it would move every control below it. */
export const isKnobInert = (segments: string[] | undefined, inView: string[]): boolean =>
  !!segments && segments.length > 0 && segments.every((id) => !inView.includes(id));

export type TTwinValues = {
  valueOf: (key: string) => number | undefined;
  unitOf?: (key: string) => string | null | undefined;
  dpOf?: (key: string) => number | null | undefined;
  setByReader?: (key: string) => boolean;
};

export const spellTwinValue = (values: TTwinValues, key: string, value: number | undefined): string => {
  if (value === undefined) return "\u2014";
  const unit = values.unitOf?.(key);
  const dp = values.dpOf?.(key);
  const format = figureFormat(dp, unit);
  return spellSlot(values.setByReader?.(key) ? { ...format, exact: true } : format, value, unit, dp);
};

export type TSpelledParts = { figure: string; unit: string | null };

const WORD_UNIT = /[\s/].*$/;

export const spellTwinParts = (values: TTwinValues, key: string, value: number | undefined): TSpelledParts => {
  const spelled = spellTwinValue(values, key, value);
  const format = value === undefined ? undefined : figureFormat(values.dpOf?.(key), values.unitOf?.(key));
  const unit = format?.unit?.match(WORD_UNIT)?.[0];
  if (!unit || !spelled.endsWith(unit)) return { figure: spelled, unit: null };
  return { figure: spelled.slice(0, -unit.length), unit: unit.trim() };
};

export const applyRefs = (blocks: TTwinBlock[], values: TTwinValues): TTwinBlock[] =>
  blocks.map((block) => {
    if (block.kind !== "headline" && !block.refs?.length) return block;
    const valueOf = (key: string) => values.valueOf(key) ?? Number.NaN;
    const filled = fillBlock(
      block as never,
      valueOf,
      (cell) => numberInCell(cell) !== null,
      values.unitOf,
      values.dpOf,
      undefined,
      values.setByReader
    ) as TTwinBlock;
    if (filled.kind !== "headline") return filled;
    return {
      ...filled,
      items: filled.items.map((item) => {
        if (!item.key || (item.value && /\d/.test(item.value))) return item;
        const unit = values.unitOf?.(item.key) ?? item.unit;
        const dp = values.dpOf?.(item.key);
        const format = { ...figureFormat(dp, unit), ...(values.setByReader?.(item.key) ? { exact: true } : {}) };
        return { ...item, value: spellSlot(format, valueOf(item.key), unit, dp), format };
      })
    };
  });

export const TWIN_LOCAL_GROUP = "twin";

export const isTwinLocal = (figure: { group?: string } | undefined): boolean => figure?.group === TWIN_LOCAL_GROUP;

export const twinFigures = (
  figures: TLedgerFigure[],
  values: ReadonlyMap<string, number>,
  vars: TRqaSimTwin["vars"] = {}
): TLedgerFigure[] => {
  const keys = new Set(figures.map((figure) => figure.key));
  const local: TLedgerFigure[] = Object.entries(vars)
    .filter(([name]) => !keys.has(name))
    .map(([name, declared]) => ({
      key: name,
      name: varName(declared) ?? name,
      group: TWIN_LOCAL_GROUP,
      unit: varUnit(declared),
      kind: "computed",
      provenance: "computed",
      value: values.get(name) ?? null,
      formula: varExpr(declared),
      dependsOn: [],
      source: null,
      description: null,
      holds: null,
      range: null,
      error: null
    }));
  return [...figures.map((figure) => ({ ...figure, value: values.get(figure.key) ?? null })), ...local];
};
